import type { CoachPublicData } from "~/shared/loaders";

// Minimal coach interface for components that don't have full CoachPublicData
export interface MinimalCoachData {
  username: string;
  hasBookingCalendar?: boolean;
  profile: {
    contactMode?: string | null;
    isPreGeneratedProfile: boolean;
    ctaButtonText?: string | null;
  };
}

/**
 * Determines if a coach should show any CTAs (contact or booking buttons)
 * based on whether they have a proper profile (not pre-generated).
 *
 * @param coach - The coach data
 * @returns true if CTAs should be shown, false otherwise
 */
export function shouldShowCoachCTAs(
  coach: CoachPublicData | MinimalCoachData,
): boolean {
  // Don't show CTAs for pre-generated profiles since coaches haven't explicitly registered
  if (coach.profile.isPreGeneratedProfile) {
    return false;
  }

  // Show CTAs if they have booking capability or messaging is enabled
  return shouldShowBookingCTAs(coach) || shouldShowMessagingCTAs(coach);
}

/**
 * Determines if a coach should show booking CTAs specifically.
 *
 * @param coach - The coach data
 * @returns true if booking CTAs should be shown, false otherwise
 */
export function shouldShowBookingCTAs(
  coach: CoachPublicData | MinimalCoachData,
): boolean {
  // Don't show booking CTAs for pre-generated profiles
  if (coach.profile.isPreGeneratedProfile) {
    return false;
  }

  return Boolean(coach.hasBookingCalendar);
}

/**
 * Determines if a coach should show messaging CTAs specifically.
 *
 * @param coach - The coach data
 * @returns true if messaging CTAs should be shown, false otherwise
 */
export function shouldShowMessagingCTAs(
  coach: CoachPublicData | MinimalCoachData,
): boolean {
  // Don't show messaging CTAs for pre-generated profiles
  if (coach.profile.isPreGeneratedProfile) {
    return false;
  }

  // The setting defaults to messages: only "booking" turns the form off.
  return coach.profile.contactMode !== "booking";
}

/**
 * Gets the appropriate CTA button text for a coach.
 *
 * @param coach - The coach data
 * @returns The button text to display
 */
export function getCoachCTAText(
  coach: CoachPublicData | MinimalCoachData,
): string {
  return coach.profile.ctaButtonText || "Contact Me";
}

/**
 * Gets the appropriate CTA link for a coach based on their contact mode.
 *
 * @param coach - The coach data
 * @returns The link path for the CTA button
 */
export function getCoachCTALink(
  coach: CoachPublicData | MinimalCoachData,
): string {
  if (coach.profile.contactMode === "messaging") {
    return `/${coach.username}/contact`;
  }
  return `/${coach.username}/book`;
}
